import { useEffect, useMemo, useState } from "react";
import { datadogRum } from "@datadog/browser-rum";
import {
  analyzeInfra,
  converseDiscovery,
  decodeIntent,
  generateQuestions,
  planPOC,
  type BriefAddendum,
  type BriefPatch,
  type BusinessReport,
  type CompanyResponse,
  type CompanyBrief,
  type ConversationTurn,
  type DiscoveryQuestion,
  type FetchSlot,
  type FetchState,
  type FollowupQuestion,
  type InfraResponse,
  type IntentResponse,
  type POCResponse,
  type Painpoint,
  type ReportField,
} from "./api";

const FETCH_LABEL: Record<FetchSlot, string> = {
  F: "Frame",
  E: "Explore",
  T: "Translate",
  C: "Connect",
  H: "Hedge",
  A: "Architecture",
  D: "Data & Telemetry",
  R: "Risk & Compliance",
  P: "Provability",
  S: "Stakeholders",
};
const FETCH_DESC: Record<FetchSlot, string> = {
  F: "이니셔티브와 성공기준",
  E: "기술/조직/프로세스 과제",
  T: "운영 부담 → KPI 영향",
  C: "Datadog 솔루션 매핑",
  H: "다음 단계와 리스크",
  A: "스택과 배포 환경",
  D: "수집 시그널과 공백",
  R: "보안·규제·PII 경계",
  P: "POV 합의 KPI·기간",
  S: "챔피언·EB·보안",
};
const FETCH_ORDER: FetchSlot[] = ["F", "E", "T", "C", "H", "A", "D", "R", "P", "S"];
const FETCH_GROUP: Record<FetchSlot, "AE" | "SE"> = {
  F: "AE", E: "AE", T: "AE", C: "AE", H: "AE",
  A: "SE", D: "SE", R: "SE", P: "SE", S: "SE",
};

const REPORT_FIELDS: { key: keyof BusinessReport; label: string; question: string }[] = [
  { key: "revenue", label: "회사 매출", question: "회사 매출은 얼마나 됩니까?" },
  { key: "recent_funding", label: "최근 투자", question: "최근 투자나 자금 조달이 있었나요?" },
  { key: "business_structure", label: "비즈니스 구조", question: "어떻게 돈을 벌고 있는 회사인가요?" },
  { key: "org_structure", label: "조직 구조", question: "조직과 엔지니어링 팀은 어떻게 구성되어 있나요?" },
];

// ---------------------------------------------------------------------------
// session
// ---------------------------------------------------------------------------
function sessionId(): string {
  const key = "discovery.session";
  let s = localStorage.getItem(key);
  if (!s) {
    s = "s-" + Math.random().toString(36).slice(2, 10);
    localStorage.setItem(key, s);
  }
  return s;
}

type BriefStore = {
  company: CompanyResponse | null;
  infra: InfraResponse | null;
  intent: IntentResponse | null;
  questions: Record<string, { at: number; data: ReturnType<typeof Date.now> extends number ? unknown : never }>;
  poc: POCResponse | null;
};

type Tab = "company" | "infra" | "intent" | "questions" | "poc";

const TABS: { id: Tab; label: string; blurb: string }[] = [
  { id: "company", label: "회사 이해하기", blurb: "비즈니스 모델과 우선순위 파악" },
  { id: "infra", label: "인프라 진단", blurb: "현재 스택과 운영 부담 분석" },
  { id: "intent", label: "발언 해석", blurb: "고객의 말 뒤에 있는 진짜 니즈" },
  { id: "questions", label: "질문 준비", blurb: "단계별 디스커버리 질문" },
  { id: "poc", label: "실행 계획", blurb: "현재 상황 기반 다음 액션" },
];

const PRESETS = [
  { name: "Toss", domain: "toss.im", ctx: "페이먼츠와 금융 슈퍼앱. K8s 위에 Java 마이크로서비스." },
  { name: "Coupang", domain: "coupang.com", ctx: "이커머스와 물류. 트래픽 피크에 민감." },
  { name: "Krafton", domain: "krafton.com", ctx: "글로벌 게임 스튜디오. 매치메이킹 레이턴시 중요." },
  { name: "Daangn", domain: "daangn.com", ctx: "로컬 중고거래. 모바일 RUM과 백엔드 Ruby/Go 혼재." },
];

export default function App() {
  const sid = useMemo(sessionId, []);
  const [tab, setTab] = useState<Tab>("company");
  const [brief, setBrief] = useState<{
    company: CompanyResponse | null;
    infra: InfraResponse | null;
    intent: IntentResponse | null;
    questions: Record<string, any>;
    poc: POCResponse | null;
  }>(() => {
    try {
      return JSON.parse(localStorage.getItem("discovery.brief") || "") as any;
    } catch {
      return { company: null, infra: null, intent: null, questions: {}, poc: null };
    }
  });

  useEffect(() => {
    localStorage.setItem("discovery.brief", JSON.stringify(brief));
  }, [brief]);

  useEffect(() => {
    datadogRum.addAction("tab.view", { tab });
  }, [tab]);

  function resetAll() {
    if (!confirm("지금까지 모은 리서치 내용을 모두 비울까요?")) return;
    setBrief({ company: null, infra: null, intent: null, questions: {}, poc: null });
  }

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-name">DiscoveryCopilot</div>
          <div className="brand-tag">고객을 깊이 이해하는 일을 돕습니다</div>
        </div>
        <nav className="nav">
          {TABS.map((n) => (
            <button
              key={n.id}
              className={`nav-btn ${tab === n.id ? "active" : ""}`}
              onClick={() => setTab(n.id)}
            >
              <div className="nav-label">{n.label}</div>
              <div className="nav-sub">{n.blurb}</div>
            </button>
          ))}
        </nav>
        <div className="sidebar-foot">
          <CurrentCompanyCard brief={brief.company} />
          <button className="btn-ghost" onClick={resetAll}>
            모두 비우기
          </button>
        </div>
      </aside>

      <main className="main">
        {tab === "company" && (
          <CompanyView
            sid={sid}
            brief={brief.company}
            setBrief={(c) => setBrief((b) => ({ ...b, company: c }))}
          />
        )}
        {tab === "infra" && (
          <InfraView
            sid={sid}
            company={brief.company}
            data={brief.infra}
            setData={(i) => setBrief((b) => ({ ...b, infra: i }))}
          />
        )}
        {tab === "intent" && (
          <IntentView
            sid={sid}
            company={brief.company}
            data={brief.intent}
            setData={(i) => setBrief((b) => ({ ...b, intent: i }))}
          />
        )}
        {tab === "questions" && (
          <QuestionsView
            sid={sid}
            company={brief.company}
            store={brief.questions}
            setStore={(q) => setBrief((b) => ({ ...b, questions: q }))}
          />
        )}
        {tab === "poc" && (
          <POCView
            sid={sid}
            company={brief.company}
            data={brief.poc}
            setData={(p) => setBrief((b) => ({ ...b, poc: p }))}
          />
        )}
      </main>
    </div>
  );
}

function CurrentCompanyCard({ brief }: { brief: CompanyResponse | null }) {
  if (!brief) {
    return (
      <div className="current-company empty">
        <div className="cc-hint">먼저 살펴볼 회사를 정해주세요.</div>
      </div>
    );
  }
  const b = brief.brief;
  return (
    <div className="current-company">
      <div className="cc-label">지금 보고 있는 회사</div>
      <div className="cc-name">{b.company_name}</div>
      <div className="cc-meta">
        <span className="chip">{b.industry}</span>
        {b.size_hint && <span className="chip chip-soft">{b.size_hint}</span>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Company view
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// v6.1 — ChatGPT-style single conversation. 프론트가 전 history 를 들고 있고,
// 매 턴 백엔드에 그대로 다시 보내준다 (서버 stateless).
// ---------------------------------------------------------------------------
type ChatMessage = {
  id: string;
  role: "user" | "assistant";
  text: string;
  // assistant 메시지에만 곁들이는 메타
  fetch_state?: FetchState;
  followups?: FollowupQuestion[];
  is_complete?: boolean;
};

type ConverseSession = {
  sid: string;
  company_name: string;
  domain?: string;
  context?: string;
  brief: CompanyBrief | null;
  fetch_state: FetchState | null;
  next_questions: FollowupQuestion[];
  addenda: BriefAddendum[]; // 누적된 보고서 증축 단락
  summary_deltas: string[];
  history: ConversationTurn[]; // 백엔드로 보낼 history (raw turns)
  messages: ChatMessage[];     // UI 에 그릴 채팅 스트림
  last_meta: { tokens: number; ms: number; turn_index: number } | null;
};

const SESSION_STORE_KEY = "discovery.session.v6_1";

function loadSession(): ConverseSession | null {
  try {
    const raw = localStorage.getItem(SESSION_STORE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as ConverseSession;
  } catch {
    return null;
  }
}

function saveSession(s: ConverseSession | null) {
  if (!s) {
    localStorage.removeItem(SESSION_STORE_KEY);
    return;
  }
  try {
    localStorage.setItem(SESSION_STORE_KEY, JSON.stringify(s));
  } catch {
    /* quota exceeded — ignore */
  }
}

// ---------------------------------------------------------------------------
// v6.1 — ChatGPT-style 단일 대화. 입력창 하나로 모든 턴을 받고, 메시지 스트림을
// 한 줄로 흘려 보낸다. 상단에는 10축 mini-bar 만 고정.
// ---------------------------------------------------------------------------
function genId(): string {
  return Math.random().toString(36).slice(2, 10) + "-" + Date.now().toString(36);
}

// 첫 입력 한 줄을 파싱해 이름/도메인을 추출.
//   "토스, toss.im, 페이먼츠 슈퍼앱" → name=토스 / domain=toss.im / ctx="페이먼츠 슈퍼앱"
//   "쿠팡" → name=쿠팡
//   "https://krafton.com" → domain=krafton.com
function parseInitialInput(text: string): {
  name: string;
  domain: string;
  context: string;
} {
  const t = text.trim();
  if (!t) return { name: "", domain: "", context: "" };
  const parts = t
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
  let name = "";
  let domain = "";
  const rest: string[] = [];
  const domainRe = /(?:https?:\/\/)?([a-z0-9-]+\.[a-z][a-z0-9.-]+)/i;
  for (const p of parts) {
    const m = p.match(domainRe);
    if (m && !domain) {
      domain = m[1].toLowerCase();
    } else if (!name) {
      name = p;
    } else {
      rest.push(p);
    }
  }
  if (!name && domain) name = domain.split(".")[0];
  return { name, domain, context: rest.join(", ") };
}

function CompanyView({
  sid,
  brief,
  setBrief,
}: {
  sid: string;
  brief: CompanyResponse | null;
  setBrief: (b: CompanyResponse | null) => void;
}) {
  const [session, setSession] = useState<ConverseSession | null>(() => loadSession());
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // 다음 답변이 어떤 질문에 대한 것인지 (칩 클릭 시 세팅). null 이면 자유 답변.
  const [pendingFor, setPendingFor] = useState<FollowupQuestion | null>(null);

  useEffect(() => {
    saveSession(session);
    if (session?.brief) {
      const synthetic: CompanyResponse = {
        ok: true,
        request_id: "v6-synthetic",
        session_id: session.sid,
        elapsed_ms: session.last_meta?.ms ?? 0,
        brief: session.brief,
        model: "v6",
        usage: {
          prompt_tokens: 0,
          completion_tokens: 0,
          total_tokens: session.last_meta?.tokens ?? 0,
        },
        sources: { web: "tavily", site_fetched: false, industry_kb: [] },
      };
      setBrief(synthetic);
    } else {
      setBrief(null);
    }
  }, [session, setBrief]);

  async function startNew(rawText: string) {
    const { name, domain, context } = parseInitialInput(rawText);
    if (!name && !domain) {
      setErr("회사명이나 도메인 중 하나는 알려주세요.");
      return;
    }
    setErr(null);
    setBusy(true);
    const userMsg: ChatMessage = {
      id: genId(),
      role: "user",
      text: rawText.trim(),
    };
    try {
      const r = await converseDiscovery({
        session_id: sid,
        company_name: name || domain,
        domain: domain || undefined,
        context: context || undefined,
        brief: null,
        history: [],
      });
      const assistantMsg: ChatMessage = {
        id: genId(),
        role: "assistant",
        text: r.assistant_message || "",
        fetch_state: r.fetch_state,
        followups: r.next_questions,
        is_complete: r.is_complete,
      };
      const fresh: ConverseSession = {
        sid,
        company_name: name || domain,
        domain: domain || undefined,
        context: context || undefined,
        brief: r.brief ?? null,
        fetch_state: r.fetch_state,
        next_questions: r.next_questions,
        addenda: [],
        summary_deltas: [],
        history: [
          { role: "agent", kind: "brief", payload: r.brief ?? null },
          { role: "agent", kind: "questions", payload: r.next_questions },
        ],
        messages: [userMsg, assistantMsg],
        last_meta: { tokens: 0, ms: r.elapsed_ms, turn_index: r.turn_index },
      };
      setSession(fresh);
      setDraft("");
      setPendingFor(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function sendAnswer(text: string) {
    if (!session || !text.trim()) return;
    setBusy(true);
    setErr(null);
    const forQuestion = pendingFor;
    const userMsg: ChatMessage = {
      id: genId(),
      role: "user",
      text: forQuestion ? `*${FETCH_LABEL[forQuestion.slot]}* — ${forQuestion.text}\n${text.trim()}` : text.trim(),
    };
    const newHistory: ConversationTurn[] = [
      ...session.history,
      forQuestion
        ? {
            role: "user",
            kind: "answered_question",
            payload: {
              slot: forQuestion.slot,
              question: forQuestion.text,
              answer: text.trim(),
            },
          }
        : { role: "user", kind: "answer", payload: text.trim() },
    ];
    // 낙관적 업데이트: user 메시지 즉시 표시
    setSession({ ...session, messages: [...session.messages, userMsg] });
    try {
      const r = await converseDiscovery({
        session_id: session.sid,
        company_name: session.company_name,
        domain: session.domain,
        context: session.context,
        brief: session.brief,
        history: newHistory,
      });
      const patch: BriefPatch = r.brief_patch ?? { addendum: [], summary_delta: "" };
      const assistantMsg: ChatMessage = {
        id: genId(),
        role: "assistant",
        text: r.assistant_message || "",
        fetch_state: r.fetch_state,
        followups: r.next_questions,
        is_complete: r.is_complete,
      };
      setSession({
        ...session,
        fetch_state: r.fetch_state,
        next_questions: r.next_questions,
        addenda: [...session.addenda, ...patch.addendum],
        summary_deltas: patch.summary_delta
          ? [...session.summary_deltas, patch.summary_delta]
          : session.summary_deltas,
        history: [
          ...newHistory,
          { role: "agent", kind: "brief_patch", payload: patch },
          { role: "agent", kind: "questions", payload: r.next_questions },
        ],
        messages: [...session.messages, userMsg, assistantMsg],
        last_meta: { tokens: 0, ms: r.elapsed_ms, turn_index: r.turn_index },
      });
      setDraft("");
      setPendingFor(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function exportMarkdown() {
    if (!brief) return;
    const md = buildMarkdown(brief, session ?? undefined);
    const blob = new Blob([md], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${brief.brief.company_name}-brief.md`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function resetSession() {
    if (!confirm("지금까지의 대화를 비우고 새 회사로 시작할까요?")) return;
    setSession(null);
    setDraft("");
    setPendingFor(null);
  }

  function onSubmit() {
    if (busy) return;
    const text = draft.trim();
    if (!text) return;
    if (!session) {
      void startNew(text);
    } else {
      void sendAnswer(text);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      onSubmit();
    }
  }

  function pickQuestion(q: FollowupQuestion) {
    setPendingFor(q);
    // draft 가 비어 있을 때만 안내 텍스트를 채워준다 (사용자 입력 보존)
    if (!draft.trim()) setDraft("");
  }

  const lastFollowups = session?.next_questions ?? [];
  const lastState = session?.fetch_state ?? null;
  const isComplete = lastState?.is_complete ?? false;

  return (
    <div className="chat-view">
      <div className="chat-head">
        <div>
          <div className="hero-eyebrow">Discovery Chat</div>
          <h1 className="chat-h1">
            {session?.company_name
              ? `${session.company_name} 디스커버리`
              : "회사 한 줄을 알려주세요."}
          </h1>
          <p className="chat-sub">
            한 번에 완성하지 않아요. 답할 수 있는 만큼만 적어 주시면, 비어 있는 칸을 같이 채워 갑니다.
            AE 의 FETCH 5축과 SE 의 5축(Architecture · Data · Risk · Provability · Stakeholders) 을 함께 봅니다.
          </p>
        </div>
        {session && (
          <div className="chat-head-actions">
            <button className="btn small" onClick={exportMarkdown}>
              Markdown 저장
            </button>
            <button className="btn ghost small" onClick={resetSession} disabled={busy}>
              새 회사
            </button>
          </div>
        )}
      </div>

      {lastState && <CoverageBar state={lastState} />}

      <div className="chat-stream">
        {!session && (
          <div className="chat-empty">
            <p>
              예시: <code>토스, toss.im, 페이먼츠 슈퍼앱</code> 또는 <code>krafton.com</code>
            </p>
            <div className="preset-row">
              {PRESETS.map((p) => (
                <button
                  key={p.name}
                  className="preset"
                  onClick={() => setDraft(`${p.name}, ${p.domain}, ${p.ctx}`)}
                  disabled={busy}
                >
                  {p.name}
                </button>
              ))}
            </div>
          </div>
        )}
        {session?.messages.map((m) => (
          <ChatBubble
            key={m.id}
            msg={m}
            onPickFollowup={pickQuestion}
            onSubmitDirect={(q, ans) => {
              setPendingFor(q);
              void sendAnswer(ans);
            }}
            disabled={busy}
          />
        ))}
        {busy && <ChatThinking />}
      </div>

      {pendingFor && (
        <div className="chat-pending">
          <span className={`followup-slot slot-${pendingFor.slot}`}>
            {FETCH_LABEL[pendingFor.slot]}
          </span>
          <span className="chat-pending-q">{pendingFor.text}</span>
          <button
            className="chat-pending-x"
            onClick={() => setPendingFor(null)}
            disabled={busy}
            aria-label="질문 선택 취소"
          >
            ×
          </button>
        </div>
      )}

      <div className="chat-composer">
        <textarea
          className="chat-input"
          rows={pendingFor ? 4 : 3}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={
            !session
              ? "회사 이름, 도메인, 한 줄 설명을 적어 주세요"
              : pendingFor
              ? "위 질문에 답하기 (모르는 건 비워 두셔도 돼요. ⌘/Ctrl+Enter 로 전송)"
              : "추가로 알고 있는 것을 적어 주세요. ⌘/Ctrl+Enter 로 전송"
          }
          disabled={busy}
        />
        <button className="btn primary" onClick={onSubmit} disabled={busy || !draft.trim()}>
          {busy ? "처리 중…" : !session ? "시작" : "보내기"}
        </button>
      </div>

      {!isComplete && lastFollowups.length > 0 && session && (
        <div className="chat-suggestions">
          <span className="chat-suggestions-label">추천 질문</span>
          {lastFollowups.slice(0, 4).map((q, i) => (
            <button
              key={i}
              className={`chat-chip${pendingFor?.text === q.text ? " active" : ""}`}
              onClick={() => pickQuestion(q)}
              disabled={busy}
              title={q.why || ""}
            >
              <span className={`chat-chip-slot slot-${q.slot}`}>{FETCH_LABEL[q.slot]}</span>
              <span className="chat-chip-text">{q.text}</span>
            </button>
          ))}
        </div>
      )}

      {err && <div className="error-box">{err}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// v6.1 — Coverage bar (10축 한 줄), Chat bubble, Chat thinking
// ---------------------------------------------------------------------------
function CoverageBar({ state }: { state: FetchState }) {
  const aeSlots = FETCH_ORDER.filter((s) => FETCH_GROUP[s] === "AE");
  const seSlots = FETCH_ORDER.filter((s) => FETCH_GROUP[s] === "SE");
  const aePct = Math.round((state.ae_avg ?? 0) * 100);
  const sePct = Math.round((state.se_avg ?? 0) * 100);
  return (
    <div className="cov-bar">
      <div className="cov-group">
        <span className="cov-group-label">AE · FETCH {aePct}%</span>
        <div className="cov-cells">
          {aeSlots.map((s) => (
            <CoverageCell key={s} slot={s} score={state.scores[s] ?? 0} note={state.notes?.[s] || ""} weakest={state.weakest === s} />
          ))}
        </div>
      </div>
      <div className="cov-divider" aria-hidden />
      <div className="cov-group">
        <span className="cov-group-label">SE · ADRPS {sePct}%</span>
        <div className="cov-cells">
          {seSlots.map((s) => (
            <CoverageCell key={s} slot={s} score={state.scores[s] ?? 0} note={state.notes?.[s] || ""} weakest={state.weakest === s} />
          ))}
        </div>
      </div>
      {state.is_complete && <span className="cov-complete">10축 모두 채워졌어요</span>}
    </div>
  );
}

function CoverageCell({
  slot,
  score,
  note,
  weakest,
}: {
  slot: FetchSlot;
  score: number;
  note: string;
  weakest: boolean;
}) {
  const tone = score >= 0.7 ? "ok" : score >= 0.4 ? "mid" : "low";
  const pct = Math.round(score * 100);
  const tip = `${slot} ${FETCH_LABEL[slot]} — ${FETCH_DESC[slot]}\n${pct}%${note ? `\n${note}` : ""}`;
  return (
    <span className={`cov-cell tone-${tone}${weakest ? " weakest" : ""}`} title={tip}>
      <span className="cov-letter">{slot}</span>
      <span className="cov-fill" style={{ width: `${Math.max(pct, 6)}%` }} />
    </span>
  );
}

// 매우 가벼운 markdown: **bold**, *italic*, 줄바꿈, "- " 리스트.
// 의존성을 추가하지 않기 위해 직접 구현.
function renderInline(s: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let i = 0;
  let key = 0;
  while (i < s.length) {
    if (s.startsWith("**", i)) {
      const end = s.indexOf("**", i + 2);
      if (end > 0) {
        out.push(<strong key={`b${key++}`}>{s.slice(i + 2, end)}</strong>);
        i = end + 2;
        continue;
      }
    }
    if (s[i] === "*") {
      const end = s.indexOf("*", i + 1);
      if (end > 0) {
        out.push(<em key={`i${key++}`}>{s.slice(i + 1, end)}</em>);
        i = end + 1;
        continue;
      }
    }
    if (s.startsWith("`", i)) {
      const end = s.indexOf("`", i + 1);
      if (end > 0) {
        out.push(<code key={`c${key++}`}>{s.slice(i + 1, end)}</code>);
        i = end + 1;
        continue;
      }
    }
    // plain run until next marker
    let j = i;
    while (j < s.length && s[j] !== "*" && s[j] !== "`") j++;
    out.push(<span key={`t${key++}`}>{s.slice(i, j)}</span>);
    i = j;
  }
  return out;
}

function renderMarkdown(text: string): React.ReactNode {
  const lines = text.split("\n");
  const blocks: React.ReactNode[] = [];
  let listBuf: string[] = [];
  let key = 0;
  function flushList() {
    if (listBuf.length === 0) return;
    blocks.push(
      <ul className="md-ul" key={`ul${key++}`}>
        {listBuf.map((item, i) => (
          <li key={i}>{renderInline(item)}</li>
        ))}
      </ul>,
    );
    listBuf = [];
  }
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (line.startsWith("- ")) {
      listBuf.push(line.slice(2));
    } else if (line.trim() === "") {
      flushList();
      blocks.push(<div className="md-spacer" key={`sp${key++}`} />);
    } else {
      flushList();
      blocks.push(<p className="md-p" key={`p${key++}`}>{renderInline(line)}</p>);
    }
  }
  flushList();
  return <>{blocks}</>;
}

function ChatBubble({
  msg,
  onPickFollowup,
  onSubmitDirect: _onSubmitDirect,
  disabled,
}: {
  msg: ChatMessage;
  onPickFollowup: (q: FollowupQuestion) => void;
  onSubmitDirect: (q: FollowupQuestion, ans: string) => void;
  disabled: boolean;
}) {
  if (msg.role === "user") {
    return (
      <div className="bubble user">
        <div className="bubble-body">{renderMarkdown(msg.text)}</div>
      </div>
    );
  }
  return (
    <div className="bubble assistant">
      <div className="bubble-body">
        {renderMarkdown(msg.text)}
        {msg.followups && msg.followups.length > 0 && !msg.is_complete && (
          <div className="bubble-chips">
            {msg.followups.slice(0, 3).map((q, i) => (
              <button
                key={i}
                className="chat-chip inline"
                onClick={() => onPickFollowup(q)}
                disabled={disabled}
                title={q.why || ""}
              >
                <span className={`chat-chip-slot slot-${q.slot}`}>{FETCH_LABEL[q.slot]}</span>
                <span className="chat-chip-text">{q.text}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ChatThinking() {
  return (
    <div className="bubble assistant">
      <div className="bubble-body thinking">
        <span className="dot" />
        <span className="dot" />
        <span className="dot" />
      </div>
    </div>
  );
}

function BusinessReportSection({
  brief,
  meta,
}: {
  brief: CompanyResponse["brief"];
  meta: { tokens: number; ms: number };
}) {
  const report = brief.business_report;
  if (!report) return null;
  return (
    <article className="report-doc">
      <header className="report-doc-head">
        <div className="report-doc-eyebrow">Discovery brief</div>
        <h2 className="report-doc-title">{brief.company_name}</h2>
        <div className="report-doc-meta">
          <span>{brief.industry}</span>
          {brief.size_hint && <span>· {brief.size_hint}</span>}
          {brief.domain && <span>· {brief.domain}</span>}
          <span className="report-doc-meta-dim">· {meta.tokens.toLocaleString()} tokens · {meta.ms}ms</span>
        </div>
      </header>

      {brief.summary && (
        <section className="report-doc-section">
          <p className="report-doc-lede">{brief.summary}</p>
        </section>
      )}

      {REPORT_FIELDS.map((f) => (
        <ReportProse key={f.key} label={f.label} field={report[f.key]} />
      ))}

      <footer className="report-doc-foot">
        매출과 최근 투자, 비즈니스 구조, 조직 구조는 공개된 정보를 모아 정리한 내용입니다.
        근거가 부족한 항목은 본문에 그대로 표시했고, 확정이 필요한 수치는 미팅에서 직접 확인해주세요.
      </footer>
    </article>
  );
}

function ReportProse({
  label,
  field,
}: {
  label: string;
  field: ReportField | undefined;
}) {
  const value = (field?.value || "").trim() || "공개된 정보에서 확인되지 않았습니다.";
  const details = (field?.details || []).filter(Boolean);
  const sources = (field?.sources || []).filter(Boolean);
  const conf = (field?.confidence || "low").toLowerCase();
  const confNote =
    conf === "high"
      ? "공개된 자료에서 확인된 내용입니다."
      : conf === "medium"
      ? "복수의 자료에서 근거를 찾았으나, 보강 확인이 권장됩니다."
      : "공개된 정보가 충분하지 않아 추정을 포함했습니다. 미팅에서 직접 확인이 필요합니다.";

  return (
    <section className={`report-doc-section conf-${conf}`}>
      <h3 className="report-doc-h3">{label}</h3>
      <p className="report-doc-p">{value}</p>
      {details.length > 0 && (
        <p className="report-doc-p report-doc-detail">
          {details.map((d, i) => (
            <span key={i}>
              {d}
              {i < details.length - 1 ? " " : ""}
            </span>
          ))}
        </p>
      )}
      <p className="report-doc-note">
        <span className={`conf-dot conf-${conf}`} aria-hidden />
        {confNote}
        {sources.length > 0 && (
          <span className="report-doc-cite">
            {" 출처 — "}
            {sources.slice(0, 3).map((s, i) => {
              let host = s;
              try {
                host = new URL(s).hostname.replace(/^www\./, "");
              } catch {
                /* keep raw */
              }
              return (
                <a
                  key={i}
                  href={s}
                  target="_blank"
                  rel="noreferrer"
                  className="report-doc-cite-link"
                >
                  {host}
                  {i < Math.min(sources.length, 3) - 1 ? ", " : ""}
                </a>
              );
            })}
          </span>
        )}
      </p>
    </section>
  );
}

function CompanyBriefPane({ r }: { r: CompanyResponse }) {
  const b = r.brief;
  return (
    <>
      <BusinessReportSection
        brief={b}
        meta={{ tokens: r.usage.total_tokens, ms: r.elapsed_ms }}
      />

      <h3 className="section-title">고민하고 있을 만한 지점 ({b.painpoints.length})</h3>
      <div className="painpoint-grid">
        {b.painpoints.map((p, i) => (
          <PainpointCard key={i} p={p} />
        ))}
      </div>

      <h3 className="section-title">읽힌 기술 신호</h3>
      <div className="signals">
        {Object.entries(b.tech_signals).length === 0 && <div className="muted">아직 또렷한 신호는 없어요.</div>}
        {Object.entries(b.tech_signals).map(([k, v]) => (
          <div key={k} className="signal">
            <div className="signal-key">{k}</div>
            <div className="signal-val">{v.join(", ")}</div>
          </div>
        ))}
      </div>

      <h3 className="section-title">첫 미팅에서 물어볼 만한 것</h3>
      <ul className="q-list">
        {b.suggested_questions.map((q, i) => (
          <li key={i}>{q}</li>
        ))}
      </ul>

      <h3 className="section-title">참고한 자료</h3>
      <ul className="source-list">
        {b.research_sources.map((s, i) => (
          <li key={i}>
            <a href={s.url} target="_blank" rel="noreferrer">
              {s.title || s.url}
            </a>
          </li>
        ))}
      </ul>
    </>
  );
}

function PainpointCard({ p }: { p: Painpoint }) {
  return (
    <div className={`painpoint sev-${p.severity}`}>
      <div className="painpoint-head">
        <span className="painpoint-title">{p.title}</span>
        <span className="sev-badge">중요도 {p.severity}/5</span>
      </div>
      <p className="painpoint-evidence">{p.evidence}</p>
      <div className="painpoint-products">
        {p.recommended_products.map((id) => (
          <span key={id} className="chip chip-soft">
            {id}
          </span>
        ))}
      </div>
    </div>
  );
}

function buildMarkdown(r: CompanyResponse, session?: ConverseSession): string {
  const b = r.brief;
  const lines: string[] = [];
  lines.push(`# ${b.company_name} — Discovery Brief`);
  if (b.domain) lines.push(`> ${b.domain}`);
  lines.push("");
  lines.push(`- Industry: **${b.industry}**`);
  if (b.size_hint) lines.push(`- Size hint: ${b.size_hint}`);
  lines.push(`- Model: ${r.model} · ${r.usage.total_tokens} tokens · ${r.elapsed_ms}ms`);
  lines.push("");
  lines.push("## Summary");
  lines.push(b.summary || "_(없음)_");
  lines.push("");
  if (b.business_report) {
    lines.push("## Business report");
    lines.push(
      "매출과 최근 투자, 비즈니스 구조, 조직 구조를 공개된 정보로 정리한 내용입니다."
    );
    lines.push("");
    for (const f of REPORT_FIELDS) {
      const item = b.business_report[f.key];
      const conf = (item?.confidence || "low").toLowerCase();
      const confNote =
        conf === "high"
          ? "공개된 자료에서 확인됨"
          : conf === "medium"
          ? "복수 자료에서 근거 확인, 보강 권장"
          : "추정 포함, 미팅에서 직접 확인 필요";
      lines.push(`### ${f.label}`);
      const value = item?.value || "공개된 정보에서 확인되지 않았습니다.";
      const detailText = item?.details?.length ? " " + item.details.join(" ") : "";
      lines.push(`${value}${detailText}`);
      lines.push("");
      const cite = item?.sources?.length
        ? ` 출처 — ${item.sources
            .slice(0, 3)
            .map((u) => {
              try {
                return new URL(u).hostname.replace(/^www\./, "");
              } catch {
                return u;
              }
            })
            .join(", ")}`
        : "";
      lines.push(`_${confNote}.${cite}_`);
      lines.push("");
    }
  }
  lines.push("## Painpoint hypotheses");
  for (const p of b.painpoints) {
    lines.push(`### [sev ${p.severity}/5] ${p.title}`);
    lines.push(p.evidence);
    lines.push(`Datadog products: ${p.recommended_products.join(", ") || "—"}`);
    lines.push("");
  }
  lines.push("## Tech signals");
  for (const [k, v] of Object.entries(b.tech_signals)) {
    lines.push(`- **${k}**: ${v.join(", ")}`);
  }
  lines.push("");
  lines.push("## Questions to ask");
  for (const q of b.suggested_questions) lines.push(`- ${q}`);
  lines.push("");
  lines.push("## Sources");
  for (const s of b.research_sources) lines.push(`- [${s.title || s.url}](${s.url})`);

  if (session) {
    if (session.fetch_state) {
      lines.push("");
      lines.push("## FETCH coverage");
      for (const slot of FETCH_ORDER) {
        const score = session.fetch_state.scores[slot] ?? 0;
        const note = session.fetch_state.notes?.[slot] || "";
        lines.push(`- **${slot} ${FETCH_LABEL[slot]}** ${(score * 100).toFixed(0)}% — ${note}`);
      }
    }
    if (session.summary_deltas.length > 0) {
      lines.push("");
      lines.push("## Conversation summary");
      for (const d of session.summary_deltas) lines.push(`- ${d}`);
    }
    if (session.addenda.length > 0) {
      lines.push("");
      lines.push("## Conversation addenda");
      for (const a of session.addenda) {
        lines.push(`### [${FETCH_LABEL[a.slot]}] ${a.heading || ""}`);
        lines.push(a.body);
        lines.push("");
      }
    }
  }

  return lines.join("\n");
}

// ---------------------------------------------------------------------------
// Infra view
// ---------------------------------------------------------------------------
function InfraView({
  sid,
  company,
  data,
  setData,
}: {
  sid: string;
  company: CompanyResponse | null;
  data: InfraResponse | null;
  setData: (i: InfraResponse | null) => void;
}) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function run() {
    if (!text.trim()) {
      setErr("현재 인프라가 어떻게 구성되어 있는지 알려주세요.")
      return;
    }
    setErr(null);
    setBusy(true);
    try {
      const r = await analyzeInfra({
        session_id: sid,
        company_name: company?.brief.company_name,
        industry: company?.brief.industry,
        infra_text: text,
      });
      setData(r);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const samples = [
    "EKS 3개 클러스터, Java Spring Boot microservice 60+. Kafka, Redis, Postgres RDS. 모니터링은 Prometheus + ELK.",
    "AWS Lambda + Step Functions 중심의 서버리스. DynamoDB, SQS. CloudWatch만 사용 중.",
    "온프레 + AWS 하이브리드. Oracle DB 레거시, Java/.NET 혼재. 최근 EKS 전환 착수.",
  ];

  return (
    <div className="view">
      <header className="hero">
        <div className="hero-eyebrow">Step 2 · 인프라 진단</div>
        <h1>그 다음, 운영팀이 매일 무엇을 견디고 있는지 봅니다.</h1>
        <p>
          현재 인프라 구성을 자유롭게 적어주시면, 어떤 부분에서 운영 부담이 누적되고 있을지,
          그리고 그 부담을 줄이려면 어떤 가시성이 우선 필요한지를 함께 정리합니다.
          제품을 들이밀기 전에, 그들의 일상을 먼저 그려보는 단계예요.
        </p>
      </header>

      <div className="card input-card">
        <label className="field">
          <span>현재 인프라와 모니터링 구성</span>
          <textarea
            className="input"
            rows={6}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="예: EKS 위에 Java 서비스 40여 개. RDS Postgres와 Redis, Kafka. 모니터링은 Prometheus와 ELK 사용 중."
            disabled={busy}
          />
        </label>
        <div className="preset-row">
          <span className="preset-label">예시</span>
          {samples.map((s, i) => (
            <button key={i} className="preset" onClick={() => setText(s)} disabled={busy}>
              예시 {i + 1}
            </button>
          ))}
        </div>
        <div className="pane-actions">
          <button className="btn primary" onClick={run} disabled={busy}>
            {busy ? "정리하고 있어요…" : "운영 부담 살펴보기"}
          </button>
          {data && (
            <button className="btn-ghost" onClick={() => setData(null)}>
              지우고 다시 적기
            </button>
          )}
        </div>
        {err && <div className="error-box">{err}</div>}
      </div>

      {busy && !data && <Skeleton lines={5} />}
      {data && <InfraPane r={data} />}
    </div>
  );
}

function InfraPane({ r }: { r: InfraResponse }) {
  return (
    <>
      <div className="card">
        <div className="eyebrow">스택 요약</div>
        <p className="lede">{r.stack_summary || "스택을 충분히 읽어내지 못했어요."}</p>
        <MetaBar extra={<span className="chip chip-soft">{r.elapsed_ms}ms · {r.usage.total_tokens} tokens</span>} />
      </div>

      <h3 className="section-title">영역별로 보이는 어려움 ({r.painpoints.length})</h3>
      <div className="painpoint-grid">
        {r.painpoints.map((p, i) => (
          <div key={i} className={`painpoint sev-${p.severity}`}>
            <div className="painpoint-head">
              <span className="painpoint-title">{p.area}</span>
              <span className="sev-badge">중요도 {p.severity}/5</span>
            </div>
            <p className="painpoint-evidence">{p.why}</p>
            <div className="painpoint-products">
              {p.datadog_products.map((id) => (
                <span key={id} className="chip chip-soft">{id}</span>
              ))}
            </div>
            {p.followup_questions?.length > 0 && (
              <ul className="mini-q">
                {p.followup_questions.map((q, j) => (
                  <li key={j}>{q}</li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>

      {r.quick_wins.length > 0 && (
        <>
          <h3 className="section-title">바로 도움이 될 만한 작은 변화</h3>
          <ul className="q-list">
            {r.quick_wins.map((q, i) => (
              <li key={i}>{q}</li>
            ))}
          </ul>
        </>
      )}

      {r.candidate_products.length > 0 && (
        <>
          <h3 className="section-title">검토할 만한 Datadog 제품</h3>
          <div className="product-grid">
            {r.candidate_products.map((p) => (
              <div key={p.id} className="product-card">
                <div className="product-name">{p.name}</div>
                <div className="product-id">{p.id}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Intent view
// ---------------------------------------------------------------------------
function IntentView({
  sid,
  company,
  data,
  setData,
}: {
  sid: string;
  company: CompanyResponse | null;
  data: IntentResponse | null;
  setData: (i: IntentResponse | null) => void;
}) {
  const [quote, setQuote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function run() {
    if (!quote.trim()) {
      setErr("고객이 했던 말을 그대로 적어주세요.");
      return;
    }
    setErr(null);
    setBusy(true);
    try {
      const r = await decodeIntent({
        session_id: sid,
        company_name: company?.brief.company_name,
        quote,
      });
      setData(r);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const samples = [
    "지난번 장애 나고 모니터링 검토 중이에요. 근데 ELK도 이미 투자해서…",
    "NewRelic 너무 비싸요. 올해 비용 절감이 가장 큰 이슈입니다.",
    "온콜이 너무 힘들어서 팀원들이 나간대요.",
  ];

  return (
    <div className="view">
      <header className="hero">
        <div className="hero-eyebrow">Step 3 · 발언 해석</div>
        <h1>고객의 말 뒤에 있는 진짜 이야기를 읽습니다.</h1>
        <p>
          미팅에서 들은 한 마디를 그대로 옮겨주세요.
          표면적인 요청과 그 아래에 숨은 진짜 걱정을 함께 분리해, 다음에 어떻게 응답하면 좋을지 같이 생각합니다.
          말 그대로 받아들이기 전에, 한 번 더 확인해보는 단계예요.
        </p>
      </header>

      <div className="card input-card">
        <label className="field">
          <span>고객이 했던 말</span>
          <textarea
            className="input"
            rows={4}
            value={quote}
            onChange={(e) => setQuote(e.target.value)}
            placeholder="고객의 말을 가능한 그대로 옮겨주세요. 의역하지 않을수록 정확해집니다."
            disabled={busy}
          />
        </label>
        <div className="preset-row">
          <span className="preset-label">예시</span>
          {samples.map((s, i) => (
            <button key={i} className="preset" onClick={() => setQuote(s)} disabled={busy}>
              예시 {i + 1}
            </button>
          ))}
        </div>
        <div className="pane-actions">
          <button className="btn primary" onClick={run} disabled={busy}>
            {busy ? "읽고 있어요…" : "다시 읽어보기"}
          </button>
          {data && (
            <button className="btn-ghost" onClick={() => setData(null)}>
              지우고 다시 적기
            </button>
          )}
        </div>
        {err && <div className="error-box">{err}</div>}
      </div>

      {busy && !data && <Skeleton lines={4} />}
      {data && <IntentPane r={data} />}
    </div>
  );
}

function IntentPane({ r }: { r: IntentResponse }) {
  const d = r.decoded;
  return (
    <>
      <div className="two-col">
        <div className="card">
          <div className="eyebrow">표면적인 요청</div>
          <p className="lede">{d.surface_request || "—"}</p>
        </div>
        <div className="card highlight">
          <div className="eyebrow">그 아래에 있는 진짜 걱정</div>
          <ul className="q-list">
            {d.hidden_needs?.map((n, i) => (
              <li key={i}>{n}</li>
            ))}
          </ul>
        </div>
      </div>

      <h3 className="section-title">읽힌 의사결정 신호 (BANT)</h3>
      <div className="bant-grid">
        {(["budget", "authority", "need", "timeline"] as const).map((k) => (
          <div key={k} className="bant-cell">
            <div className="bant-key">{k}</div>
            <div className="bant-val">{d.bant?.[k] || "—"}</div>
          </div>
        ))}
      </div>

      {d.risks?.length > 0 && (
        <>
          <h3 className="section-title">조심해서 봐야 할 지점</h3>
          <ul className="q-list warn">
            {d.risks.map((x, i) => (
              <li key={i}>{x}</li>
            ))}
          </ul>
        </>
      )}

      {d.likely_objection && (
        <>
          <h3 className="section-title">예상되는 반응과 응답 방향</h3>
          <div className="callout">{d.likely_objection}</div>
        </>
      )}

      {d.followup_questions?.length > 0 && (
        <>
          <h3 className="section-title">한 번 더 확인하면 좋을 질문</h3>
          <ul className="q-list">
            {d.followup_questions.map((q, i) => (
              <li key={i}>{q}</li>
            ))}
          </ul>
        </>
      )}

      <MetaBar extra={<span className="chip chip-soft">{r.elapsed_ms}ms · {r.usage.total_tokens} tokens</span>} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Questions view
// ---------------------------------------------------------------------------
function QuestionsView({
  sid,
  company,
  store,
  setStore,
}: {
  sid: string;
  company: CompanyResponse | null;
  store: Record<string, any>;
  setStore: (s: Record<string, any>) => void;
}) {
  const [stage, setStage] = useState<string>("discovery");
  const [focus, setFocus] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const current = store[stage] as { questions: DiscoveryQuestion[]; elapsed_ms: number; usage: any } | undefined;

  async function run() {
    setErr(null);
    setBusy(true);
    try {
      const r = await generateQuestions({
        session_id: sid,
        company_name: company?.brief.company_name,
        industry: company?.brief.industry,
        stage,
        focus: focus || undefined,
      });
      setStore({ ...store, [stage]: { questions: r.questions, elapsed_ms: r.elapsed_ms, usage: r.usage } });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="view">
      <header className="hero">
        <div className="hero-eyebrow">Step 4 · 질문 준비</div>
        <h1>다음 미팅에서 진짜 들어야 할 것을 정리합니다.</h1>
        <p>
          좋은 질문은 가정하지 않고, 유도하지 않고, 과거의 사실을 끌어냅니다.
          단계에 맞춰 그런 질문 열두 개를 준비해 드릴게요. 모든 질문에는 왜 묻는지 짧게 적혀 있습니다.
        </p>
      </header>

      <div className="card input-card">
        <div className="form-row">
          <label className="field">
            <span>미팅 단계</span>
            <select className="input" value={stage} onChange={(e) => setStage(e.target.value)} disabled={busy}>
              <option value="discovery">첫 미팅 — 비즈니스를 이해하는 자리</option>
              <option value="technical">기술 검증 — 환경과 실행 가능성 확인</option>
              <option value="decision">의사결정 — MEDDPICC 정리</option>
            </select>
          </label>
          <label className="field">
            <span>특별히 더 묻고 싶은 주제 (선택)</span>
            <input
              className="input"
              value={focus}
              onChange={(e) => setFocus(e.target.value)}
              placeholder="예: 온콜 부담을 좀 더 깊이 듣고 싶어요"
              disabled={busy}
            />
          </label>
        </div>
        <div className="pane-actions">
          <button className="btn primary" onClick={run} disabled={busy}>
            {busy ? "정리하고 있어요…" : "질문 준비하기"}
          </button>
        </div>
        {err && <div className="error-box">{err}</div>}
      </div>

      {busy && !current && <Skeleton lines={6} />}
      {current && (
        <>
          <h3 className="section-title">
            준비된 질문 ({current.questions.length})
          </h3>
          <div className="q-cards">
            {current.questions.map((q, i) => (
              <div key={i} className="q-card">
                <div className="q-num">{i + 1}</div>
                <div className="q-body">
                  <div className="q-text">{q.q}</div>
                  <div className="q-meta">
                    <span className="chip chip-soft">{q.category}</span>
                    <span className="q-why">{q.why}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <MetaBar extra={<span className="chip chip-soft">{current.elapsed_ms}ms · {current.usage?.total_tokens || 0} tokens</span>} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PoC view
// ---------------------------------------------------------------------------
function POCView({
  sid,
  company,
  data,
  setData,
}: {
  sid: string;
  company: CompanyResponse | null;
  data: POCResponse | null;
  setData: (p: POCResponse | null) => void;
}) {
  const [statusText, setStatusText] = useState("");
  const [currentStage, setCurrentStage] = useState<string>("unknown");
  const [metrics, setMetrics] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function run() {
    if (!statusText.trim()) {
      setErr("지금 PoC가 어디까지 와있는지 알려주세요.");
      return;
    }
    setErr(null);
    setBusy(true);
    try {
      const r = await planPOC({
        session_id: sid,
        company_name: company?.brief.company_name,
        current_stage: currentStage,
        status_text: statusText,
        success_metrics: metrics
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean),
      });
      setData(r);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="view">
      <header className="hero">
        <div className="hero-eyebrow">Step 5 · 실행 계획</div>
        <h1>마지막으로, 다음 두 주를 함께 정리합니다.</h1>
        <p>
          지금 PoC가 어디까지 왔는지, 무엇이 멈춰 있는지를 적어주세요.
          현재 상황을 살펴보고 가장 작은 다음 한 걸음과, 동시에 살펴봐야 할 리스크를 함께 정리합니다.
          진도를 빨리 빼는 것보다, 다음 한 걸음이 안전한지 보는 단계예요.
        </p>
      </header>

      <div className="card input-card">
        <div className="form-row">
          <label className="field">
            <span>현재 단계</span>
            <select className="input" value={currentStage} onChange={(e) => setCurrentStage(e.target.value)} disabled={busy}>
              <option value="unknown">자동 판단</option>
              <option value="agent_install">에이전트 설치</option>
              <option value="apm">APM 적용</option>
              <option value="logs">Logs 적용</option>
              <option value="rum">RUM 적용</option>
              <option value="slo">SLO / 대시보드 정리</option>
              <option value="wrapup">마무리 단계</option>
            </select>
          </label>
          <label className="field">
            <span>합의된 성공 기준 (한 줄에 하나씩)</span>
            <textarea
              className="input"
              rows={3}
              value={metrics}
              onChange={(e) => setMetrics(e.target.value)}
              placeholder={"MTTR 30분 → 10분\n장애 인지 시간 5분 → 2분"}
              disabled={busy}
            />
          </label>
        </div>
        <label className="field">
          <span>지금까지 진행한 일과 막혀 있는 지점</span>
          <textarea
            className="input"
            rows={5}
            value={statusText}
            onChange={(e) => setStatusText(e.target.value)}
            placeholder="에이전트 설치 완료. Java 서비스 두 개에 APM 적용 중. 태깅 규칙이 없어서 서비스 맵이 정리되지 않음."
            disabled={busy}
          />
        </label>
        <div className="pane-actions">
          <button className="btn primary" onClick={run} disabled={busy}>
            {busy ? "정리하고 있어요…" : "다음 한 걸음 정리하기"}
          </button>
          {data && (
            <button className="btn-ghost" onClick={() => setData(null)}>
              지우고 다시 적기
            </button>
          )}
        </div>
        {err && <div className="error-box">{err}</div>}
      </div>

      {busy && !data && <Skeleton lines={5} />}
      {data && <POCPane r={data} />}
    </div>
  );
}

function POCPane({ r }: { r: POCResponse }) {
  const plan = r.plan;
  return (
    <>
      <div className="card">
        <div className="card-head">
          <div>
            <div className="eyebrow">현재 단계 · {r.stage}</div>
            <p className="lede">{plan.exec_summary || plan.stage || "요약을 가져오지 못했어요."}</p>
          </div>
          <div className="chip-col">
            {typeof plan._risk_score === "number" && (
              <span className={`chip risk-${plan._risk_score}`}>리스크 {plan._risk_score}/5</span>
            )}
            <span className="chip chip-soft">{r.elapsed_ms}ms · {r.usage.total_tokens} tokens</span>
          </div>
        </div>
      </div>

      {r.blockers.length > 0 && (
        <>
          <h3 className="section-title">지금 진도를 막고 있는 것</h3>
          <div className="chips">
            {r.blockers.map((b) => (
              <span key={b} className="chip warn">{b}</span>
            ))}
          </div>
        </>
      )}

      <h3 className="section-title">다음 한 걸음 ({plan.next_actions?.length || 0})</h3>
      <div className="action-list">
        {plan.next_actions?.map((a, i) => (
          <div key={i} className="action">
            <div className="action-head">
              <span className="action-title">{a.title}</span>
              <span className="chip chip-soft">{a.owner}</span>
              <span className="chip chip-soft">{a.due}</span>
            </div>
            <div className="action-check">완료 기준 — {a.success_check}</div>
          </div>
        ))}
      </div>

      {plan.risks?.length > 0 && (
        <>
          <h3 className="section-title">함께 챙겨봐야 할 것</h3>
          <ul className="q-list warn">
            {plan.risks.map((x, i) => (
              <li key={i}>{x}</li>
            ))}
          </ul>
        </>
      )}

      {plan.expansion_hooks?.length > 0 && (
        <>
          <h3 className="section-title">자연스럽게 이어질 만한 다음 주제</h3>
          <ul className="q-list">
            {plan.expansion_hooks.map((x, i) => (
              <li key={i}>{x}</li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// common
// ---------------------------------------------------------------------------
function MetaBar({ extra }: { extra?: React.ReactNode }) {
  if (!extra) return null;
  return <div className="meta-bar">{extra}</div>;
}

function Skeleton({ lines = 4 }: { lines?: number }) {
  return (
    <div className="skeleton">
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className="skeleton-line"
          style={{ width: `${95 - i * 8}%` }}
        />
      ))}
    </div>
  );
}
